import { MetricMonthly } from "callable-data-objects";
import { DBReference } from "callable-data-objects/dist/models/accountDevice/AccountDevice";
import AccountDetails from "Components/Account/AccountDetails";
import { useAccount, useOrganisation, useOrgMetrics } from "Hooks/Endpoints";
import { APIOrganisationMetricsProps } from "Providers/types";
import React, { Fragment } from "react";
import { Button, Container, Label, Table } from "semantic-ui-react";
import NumberFormat from "react-number-format";
import useCheckRole from "Hooks/APICheckRole";

const OrganisationCustomers = () => {
  const { organisation } = useOrganisation();
  const { select, account, unselect, submitting } = useAccount();
  const { monthlyMetrics }: APIOrganisationMetricsProps = useOrgMetrics();
  const { hasRole, roles } = useCheckRole();

  const getAccountMetricCategories = (
    ref: DBReference,
    monthlyMetrics: MetricMonthly
  ) => {
    const metrics = {
      cost: {
        softwareTTS: 0,
        softwareRecording: 0,
        callsIngress: 0,
        callsLocal: 0,
        callsInternational: 0,
        total: 0,
      },
      qty: {
        softwareTTS: 0,
        softwareRecording: 0,
        callsIngress: 0,
        callsLocal: 0,
        callsInternational: 0,
        total: 0,
      },
    };

    if (!hasRole(roles.organisation.charge.roles.view)) {
      return metrics;
    }

    monthlyMetrics?.summaries?.find((summ) => summ.account.id === ref.id)
      ?.metrics.forEach((m) => {
        if (m.category === "category" && m.type === "total-NB") {
          if (m.product.startsWith("SoftwareTTS")) {
            metrics.cost.softwareTTS += parseFloat(m.qty);
            metrics.cost.total += parseFloat(m.qty);
          } else if (m.product.startsWith("SoftwareRecording")) {
            metrics.cost.softwareRecording += parseFloat(m.qty);
            metrics.cost.total += parseFloat(m.qty);
          } else if (m.product.startsWith("CallsUnitedKingdom")) {
            metrics.cost.callsLocal += parseFloat(m.qty);
            metrics.cost.total += parseFloat(m.qty);
          } else if (m.product.startsWith("CallsIngress")) {
            metrics.cost.callsIngress += parseFloat(m.qty);
            metrics.cost.total += parseFloat(m.qty);
          } else if (m.product.startsWith("Calls")) {
            metrics.cost.callsInternational += parseFloat(m.qty);
            metrics.cost.total += parseFloat(m.qty);
          }
        } else if (m.category === "category" && m.type === "qty-NB") {
          if (m.product.startsWith("SoftwareTTS")) {
            metrics.qty.softwareTTS += parseFloat(m.qty);
            metrics.qty.total += parseFloat(m.qty);
          } else if (m.product.startsWith("Software")) {
            metrics.qty.softwareRecording += parseFloat(m.qty);
            metrics.qty.total += parseFloat(m.qty);
          } else if (m.product.startsWith("CallsUnitedKingdom")) {
            metrics.qty.callsLocal += parseFloat(m.qty);
            metrics.qty.total += parseFloat(m.qty);
          } else if (m.product.startsWith("CallsIngress")) {
            metrics.qty.callsIngress += parseFloat(m.qty);
            metrics.qty.total += parseFloat(m.qty);
          } else if (m.product.startsWith("Calls")) {
            metrics.qty.callsInternational += parseFloat(m.qty);
            metrics.qty.total += parseFloat(m.qty);
          }
        }
      });

    return metrics;
  };

  return (
    <Container fluid>
      {!account && monthlyMetrics && (
        <Table size='small' stackable striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                textAlign='center'
                rowSpan='2'
                style={{ borderRight: "solid #ccc" }}
              >
                Name
              </Table.HeaderCell>

              <Table.HeaderCell
                rowSpan='1'
                colspan='5'
                textAlign='center'
                style={{ borderRight: "solid #ccc" }}
              >
                This Month Qty <Label color='red'>Beta</Label>
              </Table.HeaderCell>

              {hasRole(roles.organisation.charge.roles.view) && (
                <Table.HeaderCell
                  rowSpan='1'
                  colspan='4'
                  textAlign='center'
                  style={{ borderRight: "solid #ccc" }}
                >
                  This Month Totals <Label color='red'>Beta</Label>
                </Table.HeaderCell>
              )}

              <Table.HeaderCell textAlign='center' rowSpan='2'>
                Actions
              </Table.HeaderCell>
            </Table.Row>

            <Table.Row>
              <Table.HeaderCell
                textAlign='center'
                style={{ borderRight: "solid #ccc" }}
              >
                TTS
              </Table.HeaderCell>
              <Table.HeaderCell
                textAlign='center'
                style={{ borderRight: "solid #ccc" }}
              >
                Recording
              </Table.HeaderCell>
              <Table.HeaderCell
                textAlign='center'
                style={{ borderRight: "solid #ccc" }}
              >
                Inbound
              </Table.HeaderCell>
              <Table.HeaderCell
                textAlign='center'
                style={{ borderRight: "solid #ccc" }}
              >
                Local
              </Table.HeaderCell>
              <Table.HeaderCell
                textAlign='center'
                style={{ borderRight: "solid #ccc" }}
              >
                International
              </Table.HeaderCell>
              {hasRole(roles.organisation.charge.roles.view) && (
                <Fragment>
                  <Table.HeaderCell
                    textAlign='center'
                    style={{ borderRight: "solid #ccc" }}
                  >
                    TTS
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    textAlign='center'
                    style={{ borderRight: "solid #ccc" }}
                  >
                    Recording
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    textAlign='center'
                    style={{ borderRight: "solid #ccc" }}
                  >
                    Local
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    textAlign='center'
                    style={{ borderRight: "solid #ccc" }}
                  >
                    International
                  </Table.HeaderCell>
                </Fragment>
              )}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {organisation.customers
              .map((ref: DBReference) => {
                const stat = getAccountMetricCategories(ref, monthlyMetrics);
                return {
                  ...ref,
                  ...stat,
                };
              })
              .sort((a: any, b: any) => {
                if (a.total < b.total) {
                  return 1;
                }
                if (a.total > b.total) {
                  return -1;
                }
                return 0;
              })
              .map((acc: any) => {
                return (
                  <Table.Row key={acc.id}>
                    <Table.Cell
                      style={{ borderRight: "solid #ccc" }}
                      width={10}
                    >
                      <a href='#' onClick={() => select(acc.id)}>
                        {acc.name}
                      </a>
                    </Table.Cell>

                    <Table.Cell
                      style={{ borderRight: "solid #ccc" }}
                      textAlign='center'
                      width={1}
                    >
                      <NumberFormat
                        value={acc.qty.softwareTTS}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={3}
                      />
                    </Table.Cell>
                    <Table.Cell
                      style={{ borderRight: "solid #ccc" }}
                      textAlign='center'
                      width={1}
                    >
                      <NumberFormat
                        value={acc.qty.softwareRecording}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={3}
                      />
                    </Table.Cell>
                    <Table.Cell
                      style={{ borderRight: "solid #ccc" }}
                      textAlign='center'
                      width={1}
                    >
                      <NumberFormat
                        value={acc.qty.callsIngress}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={3}
                      />
                    </Table.Cell>
                    <Table.Cell
                      style={{ borderRight: "solid #ccc" }}
                      textAlign='center'
                      width={1}
                    >
                      <NumberFormat
                        value={acc.qty.callsLocal}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={3}
                      />
                    </Table.Cell>
                    <Table.Cell
                      textAlign='center'
                      style={{ borderRight: "solid #ccc" }}
                      width={1}
                    >
                      <NumberFormat
                        value={acc.qty.callsInternational}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={3}
                      />
                    </Table.Cell>

                    {hasRole(roles.organisation.charge.roles.view) && (
                      <Table.Cell
                        style={{ borderRight: "solid #ccc" }}
                        textAlign='center'
                        width={1}
                      >
                        <NumberFormat
                          value={acc.cost.softwareTTS}
                          displayType={"text"}
                          thousandSeparator={true}
                          decimalScale={3}
                        />
                      </Table.Cell>
                    )}
                    {hasRole(roles.organisation.charge.roles.view) && (
                      <Table.Cell
                        style={{ borderRight: "solid #ccc" }}
                        textAlign='center'
                        width={1}
                      >
                        <NumberFormat
                          value={acc.cost.softwareRecording}
                          displayType={"text"}
                          thousandSeparator={true}
                          decimalScale={3}
                        />
                      </Table.Cell>
                    )}
                    {hasRole(roles.organisation.charge.roles.view) && (
                      <Table.Cell
                        style={{ borderRight: "solid #ccc" }}
                        textAlign='center'
                        width={1}
                      >
                        <NumberFormat
                          value={acc.cost.callsLocal}
                          displayType={"text"}
                          thousandSeparator={true}
                          decimalScale={3}
                        />
                      </Table.Cell>
                    )}
                    {hasRole(roles.organisation.charge.roles.view) && (
                      <Table.Cell
                        textAlign='center'
                        style={{ borderRight: "solid #ccc" }}
                        width={1}
                      >
                        <NumberFormat
                          value={acc.cost.callsInternational}
                          displayType={"text"}
                          thousandSeparator={true}
                          decimalScale={3}
                        />
                      </Table.Cell>
                    )}

                    <Table.Cell width={1}>
                      <Button primary onClick={() => select(acc.id)}>
                        View
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      )}
      {account && (
        <AccountDetails
          account={account}
          unselect={unselect}
          submitting={submitting}
        />
      )}
    </Container>
  );
};

export default OrganisationCustomers;
